import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Car, Settings, TestTube, Activity, Globe, Bell, FileText, ExternalLink } from 'lucide-react';

const TagSystemsPage = () => {
  const { hasGlobalRole } = useAuth();

  if (!hasGlobalRole('portal_admin')) {
    return (
      <div className="text-center py-8">
        <h1 className="text-2xl font-bold mb-4">Acesso negado</h1>
        <p className="text-muted-foreground">
          Você não tem permissão para acessar as configurações de sistemas de tags.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Integrações com Sistemas de Tags</h1>
        <p className="text-muted-foreground">
          Configure as integrações com provedores de tags eletrônicas para pedágios e estacionamentos.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Veloe Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              Veloe
            </CardTitle>
            <CardDescription>
              Integração com a API Veloe para gestão de tags e transações de pedágio.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-600 border-red-600">
                Desconectado
              </Badge>
              <span className="text-sm text-muted-foreground">
                Última sincronização: Nunca
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm">
                <Settings className="mr-2 h-4 w-4" />
                Configurar API
              </Button>
              <Button size="sm" variant="outline" disabled>
                <TestTube className="mr-2 h-4 w-4" />
                Testar Conexão
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sem Parar Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-orange-600" />
              Sem Parar
            </CardTitle>
            <CardDescription>
              Integração com a API Sem Parar para gestão de tags e transações.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-600 border-red-600">
                Desconectado
              </Badge>
              <span className="text-sm text-muted-foreground">
                Última sincronização: Nunca
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm">
                <Settings className="mr-2 h-4 w-4" />
                Configurar API
              </Button>
              <Button size="sm" variant="outline" disabled>
                <TestTube className="mr-2 h-4 w-4" />
                Testar Conexão
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ConnectCar Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="h-5 w-5 text-green-600" />
              ConnectCar
            </CardTitle>
            <CardDescription>
              Integração com a API ConnectCar para gestão de tags e estacionamentos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-red-600 border-red-600">
                Desconectado
              </Badge>
              <span className="text-sm text-muted-foreground">
                Última sincronização: Nunca
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm">
                <Settings className="mr-2 h-4 w-4" />
                Configurar API
              </Button>
              <Button size="sm" variant="outline" disabled>
                <TestTube className="mr-2 h-4 w-4" />
                Testar Conexão
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Dashboard */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Status das Integrações
            </CardTitle>
            <CardDescription>
              Monitoramento geral das integrações de tags eletrônicas.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span>Total de Tags Ativas:</span>
                <span className="font-medium">0</span>
              </div>
              <div className="flex justify-between">
                <span>Transações do Mês:</span>
                <span className="font-medium">0</span>
              </div>
              <div className="flex justify-between">
                <span>Últimas Falhas:</span>
                <span className="font-medium">0</span>
              </div>
            </div>
            <Button size="sm" variant="outline" className="w-full">
              <Activity className="mr-2 h-4 w-4" />
              Ver Relatório Completo
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Global Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Configurações Globais
          </CardTitle>
          <CardDescription>
            Configure comportamentos gerais das integrações com sistemas de tags.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <h4 className="font-medium">Sincronização</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Frequência de Sincronização:</span>
                  <Badge variant="secondary">15 minutos</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Sincronização Automática:</span>
                  <Badge variant="secondary">Ativada</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Mapeamento de Veículos:</span>
                  <Badge variant="secondary">Automático</Badge>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Webhooks e Notificações</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Webhook URL:</span>
                  <Badge variant="outline">Não configurado</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Notificações de Falha:</span>
                  <Badge variant="secondary">Ativadas</Badge>
                </div>
                <div className="flex justify-between">
                  <span>Logs de Transação:</span>
                  <Badge variant="secondary">30 dias</Badge>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline">
              <Settings className="mr-2 h-4 w-4" />
              Configurar Sincronização
            </Button>
            <Button variant="outline">
              <Bell className="mr-2 h-4 w-4" />
              Configurar Webhooks
            </Button>
            <Button variant="outline">
              <FileText className="mr-2 h-4 w-4" />
              Ver Logs
            </Button>
            <Button variant="outline">
              <ExternalLink className="mr-2 h-4 w-4" />
              Documentação
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TagSystemsPage;